import {
  State,
  appLogout,
  injectSlice,
  createSlice,
  createAsyncThunk,
  createAction,
} from '@bonanza-social/toolkit/state';
import { selectPrizeWallCampaignId } from '../prizeWallCampaign/prizeWallCampaign.slice';
import { parsePrizeWall } from './prizeWall.utils';
import {
  PrizeWallState,
  UpdatePrizeWallAction,
  DecreaseQuantityAction,
  SetLockedRewardIdAction,
  PrizeWallPrizeId,
  SetEligibilityErrorAction,
} from './prizeWall.types';
import PrizeWallCampaignService from '../../services/PrizeWallCampaignService/PrizeWallCampaignService';

export const prizeWallCashRewardWon = createAction('prizeWall/cashRewardWon');

export const prizeWallCoinsRewardWon = createAction('prizeWall/coinsRewardWon');

export const fetchPrizeWall = createAsyncThunk(
  'prizeWall/fetch',
  (_, thunkApi) => {
    const campaignId = selectPrizeWallCampaignId(<State>thunkApi.getState());

    if (campaignId === undefined) {
      throw new Error('Prize campaign id is missing');
    }

    return PrizeWallCampaignService.getWall({
      pathParams: {
        campaignId,
      },
    });
  },
);

export const fetchPrizeWallReward = createAsyncThunk(
  'prizeWall/fetchReward',
  (_, thunkApi) => {
    const wallId = selectPrizeWallId(<State>thunkApi.getState());
    const campaignId = selectPrizeWallCampaignId(<State>thunkApi.getState());

    if (campaignId === undefined) {
      throw new Error('Prize campaign id is missing');
    }

    if (wallId === undefined) {
      throw new Error('Prize wall id is missing');
    }

    return PrizeWallCampaignService.getWallReward({
      pathParams: {
        wallId,
        campaignId,
      },
    });
  },
);

const initialState: PrizeWallState = {
  data: undefined,
  dataState: 'idle',
  lockedRewardId: undefined,
  eligibilityError: undefined,
};

const prizeWall = createSlice({
  name: 'prizeWall',
  initialState,
  reducers: {
    updatePrizeWall(state, { payload }: UpdatePrizeWallAction) {
      if (state.data && state.data.id === payload.id) {
        const { state: nextState, winners, rewards } = payload;
        const shouldPreloadQuantities =
          state.data.state === 'Pending' && nextState === 'Open';

        state.data.state = nextState;
        state.data.winners.push(...winners);

        rewards.forEach(({ rewardId, remainingQuantity }) => {
          const localReward = state.data?.rewards.byId[rewardId];
          const isLocked = rewardId === state.lockedRewardId;

          if (localReward && !isLocked) {
            localReward.remainingQuantity = shouldPreloadQuantities
              ? remainingQuantity
              : Math.min(remainingQuantity, localReward.remainingQuantity);
          }
        });
      }
    },
    decreaseQuantity(state, { payload }: DecreaseQuantityAction) {
      if (state.data) {
        state.data.rewards.byId[payload].remainingQuantity--;
      }
    },
    setLockedRewardId(state, { payload }: SetLockedRewardIdAction) {
      state.lockedRewardId = payload;
    },
    setEligibilityError(state, { payload }: SetEligibilityErrorAction) {
      state.eligibilityError = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPrizeWall.pending, (state) => {
      state.dataState = 'pending';
    });

    builder.addCase(fetchPrizeWall.fulfilled, (state, { payload }) => {
      state.data = parsePrizeWall(payload);
      state.dataState = 'fulfilled';
    });

    builder.addCase(fetchPrizeWall.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const withPrizeWall = injectSlice({
  reducerPath: 'prizeWall',
  reducer: prizeWall.reducer,
});

export const {
  updatePrizeWall,
  setLockedRewardId,
  setEligibilityError,
  decreaseQuantity,
} = prizeWall.actions;

export const selectPrizeWallDataState = withPrizeWall.selector(
  (state) => state.prizeWall.dataState,
);

export const selectPrizeWallId = withPrizeWall.selector(
  (state) => state.prizeWall.data?.id,
);

export const selectPrizeWallState = withPrizeWall.selector(
  (state) => state.prizeWall.data?.state,
);

export const selectPrizeWallCostOfPlay = withPrizeWall.selector(
  (state) => state.prizeWall.data?.costOfPlay,
);

export const selectPrizeWallWinProbability = withPrizeWall.selector(
  (state) => state.prizeWall.data?.winProbability,
);

export const selectPrizeWallPrize = (rewardId: PrizeWallPrizeId) =>
  withPrizeWall.selector(
    (state) => state.prizeWall.data?.rewards.byId[rewardId],
  );

export const selectPrizeWallPrizes = withPrizeWall.selector(
  (state) => state.prizeWall.data?.rewards,
);

export const selectPrizeWallHeaderImage = withPrizeWall.selector(
  (state) => state.prizeWall.data?.headerImage,
);

export const selectPrizeWallBackgroundImage = withPrizeWall.selector(
  (state) => state.prizeWall.data?.backgroundImage,
);

export const selectPrizeWallOpenDate = withPrizeWall.selector(
  (state) => state.prizeWall.data?.openDate,
);

export const selectPrizeWallQuickRules = withPrizeWall.selector(
  (state) => state.prizeWall.data?.quickRules,
);

export const selectPrizeWallWinners = withPrizeWall.selector(
  (state) => state.prizeWall.data?.winners,
);

export const selectPrizeWallEligibilityError = withPrizeWall.selector(
  (state) => state.prizeWall.eligibilityError,
);

declare global {
  interface LazyLoadedSlices {
    prizeWall: ReturnType<typeof prizeWall.reducer>;
  }
}
